import { Icon, Input, type SingleComboboxOption, Switch } from '@sr/ui-library';
import { useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { AppSearchInput } from '../../../components/AppSearchInput';
import {
  FilterCombobox,
  UNNAMED_CLIENT_LABEL,
  withSelectedOptions,
} from '../../../components/FilterCombobox';
import { useClientNameSearch } from '../../../hooks/useClientNameSearch';
import { useClientNamesByIds } from '../../../hooks/useClientNames';
import { SEARCH_DEBOUNCE_MS } from '../../../hooks/useDebouncedUrlParam';
import {
  CLAIM_STATUS_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
  SERVICE_OPTIONS,
  YEAR_LENGTH,
} from './titleFilterOptions';
import type { TitleFilter, TitleFilters } from './useTitleFilters';

type TitlesFilterBarProps = {
  disabled: boolean;
  /** Filter controls only: set when a search returns nothing to narrow. */
  filtersDisabled: boolean;
  isSearchFetching: boolean;
  /** Only agent workspaces own clients, so only they get the Client filter. */
  showClientFilter: boolean;
  /**
   * The whole `useTitleFilters` value. Passed as one prop because the search
   * input and the five filters are only ever read here — the list page uses the
   * same hook's `name`/`page`/`queryFilters` for its query.
   */
  titleFilters: TitleFilters;
  /** Scopes the Client control's own client search to this workspace. */
  workspaceId?: string;
};

/**
 * The Client filter. An agent can own thousands of clients, so this one searches
 * the backend as the user types rather than rendering a truncated page.
 */
const ClientFilterCombobox = ({
  disabled,
  filter,
  isClientFilterAvailable,
  workspaceId,
}: {
  disabled: boolean;
  filter: TitleFilter;
  isClientFilterAvailable: boolean;
  workspaceId?: string;
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue(
    searchValue.trim(),
    SEARCH_DEBOUNCE_MS,
  );

  const { clientOptions, clientsQuery } = useClientNameSearch({
    enabled: isClientFilterAvailable,
    searchTerm: debouncedSearch,
    workspaceId,
  });

  // Keyed off whether the workspace has clients at all, never the current option
  // count — a term matching nothing would otherwise disable the box being typed in.
  const baseline = useClientNameSearch({
    enabled: isClientFilterAvailable,
    searchTerm: '',
    workspaceId,
  });

  // Only a *successful* empty response means no clients; an error must not
  // disable the control the user needs in order to retry.
  const hasNoClients =
    baseline.clientsQuery.isLoading ||
    (baseline.clientsQuery.isSuccess && baseline.totalClients === 0);

  /**
   * cmdk filters on every keystroke while the query behind it is debounced, so
   * without a pending row the gap reads as "No results found."
   */
  const isSearchStale =
    searchValue.trim() !== debouncedSearch || clientsQuery.isFetching;

  // Same ids the prune resolves, so one request serves both and the trigger's
  // label can never disagree with what the prune decided.
  const { clientNameById } = useClientNamesByIds(filter.selected, {
    enabled: isClientFilterAvailable,
    workspaceId,
  });

  const options = withSelectedOptions(
    clientOptions,
    filter.selected,
    (id) => clientNameById.get(id)?.clientName ?? UNNAMED_CLIENT_LABEL,
  );

  const pendingOption: SingleComboboxOption[] =
    isSearchStale && searchValue.trim()
      ? [
          {
            // Untrimmed: cmdk scores against the raw value, so a trimmed one
            // drops the row for any leading whitespace. Unguarded by a spec.
            disabled: true,
            element: <span className="text-muted-foreground">Searching…</span>,
            label: 'Searching…',
            value: searchValue,
          },
        ]
      : [];

  return (
    <FilterCombobox
      aria-label="Client"
      // All the combobox wiring lives in `FilterCombobox`; this control only
      // adds the server search on top.
      disabled={disabled || hasNoClients}
      filter={filter}
      hasSearch
      options={[...options, ...pendingOption]}
      placeholder="Client"
      popoverProps={{
        onOpenChange: (open) => {
          if (open) return;

          /*
           * The options come from the debounced value, so clearing the raw one
           * alone leaves the closed term's page against an empty box. The
           * setter is itself debounced, so it needs flushing — cancelling and
           * re-calling it only starts a fresh timer.
           */
          setSearchValue('');
          setDebouncedSearch('');
          setDebouncedSearch.flush();
        },
      }}
      searchValue={searchValue}
      selectedPlaceholder="Clients"
      setSearchValue={setSearchValue}
      // `useCombobox` re-sorts by `value`, which for clients is the uuid. The
      // list arrives already sorted by name, so leave it alone.
      sorting="none"
    />
  );
};

/**
 * Search field plus the Titles L1 filters. The filter row is collapsed behind a
 * toggle: switching it off only hides the controls, so selections stay applied
 * and stay in the URL.
 */
export const TitlesFilterBar = ({
  disabled,
  filtersDisabled,
  isSearchFetching,
  showClientFilter,
  titleFilters,
  workspaceId,
}: TitlesFilterBarProps) => {
  const { filters, hasFilterSelection, searchInput, setSearchInput } =
    titleFilters;
  const productionYear = filters.productionYear;

  // A link arriving with filters already applied opens the row, so the user can
  // see why the list is narrowed.
  const [isFiltersOpen, setIsFiltersOpen] = useState(hasFilterSelection);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <AppSearchInput
          classes={{ wrapper: 'md:w-96' }}
          disabled={disabled}
          isFetching={isSearchFetching}
          placeholder="Search your titles by name or reference"
          value={searchInput}
          onChange={setSearchInput}
        />

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch
            aria-label="Filters"
            checked={isFiltersOpen}
            disabled={disabled}
            onCheckedChange={setIsFiltersOpen}
          />
          Filters
        </label>
      </div>

      {isFiltersOpen && (
        // Two per row on phones (so five filters land 2/2/1, four 2/2), back to
        // a single wrapping row from `sm` up.
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center">
          {showClientFilter && (
            <ClientFilterCombobox
              disabled={filtersDisabled}
              filter={filters.client}
              isClientFilterAvailable={showClientFilter}
              workspaceId={workspaceId}
            />
          )}

          <FilterCombobox
            aria-label="Service"
            disabled={filtersDisabled}
            filter={filters.service}
            options={SERVICE_OPTIONS}
            placeholder="Service"
            selectedPlaceholder="Services"
          />

          <FilterCombobox
            aria-label="Program type"
            disabled={filtersDisabled}
            filter={filters.programType}
            options={PROGRAM_TYPE_OPTIONS}
            placeholder="Program type"
            selectedPlaceholder="Program types"
          />

          <Input
            aria-label="Production year"
            classes={{ wrapper: 'sm:min-w-44 sm:flex-1' }}
            // Same rule as the comboboxes: a control still holding a value must
            // stay usable, or the filter that emptied the list can't be undone.
            disabled={filtersDisabled && !productionYear.value}
            // Typed, but only ever a year: digits only, four of them. Keeps the
            // field from holding text that could never match a title. Bounded in
            // `onChange` rather than with `maxLength`, which the browser applies
            // to the raw value *before* the non-digits are stripped — pasting
            // `(2009)` would truncate to `(200` and commit nothing.
            inputMode="numeric"
            placeholder="Production year"
            sectionRight={
              productionYear.value && (
                <button
                  aria-label="Clear production year"
                  type="button"
                  onClick={() => productionYear.setValue('')}
                >
                  <Icon icon="close" size="sm" />
                </button>
              )
            }
            sectionRightPointer
            sectionRightPointerEvents
            value={productionYear.value}
            onChange={(e) =>
              productionYear.setValue(
                e.target.value.replace(/\D/g, '').slice(0, YEAR_LENGTH),
              )
            }
          />

          <FilterCombobox
            aria-label="Claim status"
            // Last in the grid: with the Client filter present that makes five,
            // so span both phone columns rather than sit half-width alone.
            className={showClientFilter ? 'col-span-2 sm:col-span-1' : ''}
            disabled={filtersDisabled}
            filter={filters.claimStatus}
            options={CLAIM_STATUS_OPTIONS}
            placeholder="Claim status"
            selectedPlaceholder="Claim statuses"
          />
        </div>
      )}
    </div>
  );
};
